import { enumIncludes } from './../../utils';
import { ReservationRequestAction, ReservationType, } from './types';
import { slugError } from '../../internals/errors/errors';
/**
 *  a reservation request holds the data and promise for a reservation request.
 *  It also exposes the resolve and reject methods of the promise.
 *
 *  A reservation request can be created to fetch reservations in bulk for the same type and guid.
 *  This number of reservations amount could be used to either fetch all reservations for a group ticket (E.g.: 1 click -> 5 tickets).
 *
 *  @internal
 */
export default class ReservationRequest {
    constructor(type, guid, numberOfReservations) {
        this._resolve = () => { };
        this._reject = () => { };
        if (!type || !enumIncludes(ReservationType, type)) {
            throw slugError('Invalid reservation request -> invalid type', ['reservator', 'request', 'constructor', 'error', 'type']);
        }
        // noinspection SuspiciousTypeOfGuard
        if (!guid || typeof guid !== 'string' || !guid.length) {
            throw slugError('Invalid reservation request -> invalid guid', ['reservator', 'request', 'constructor', 'error', 'guid']);
        }
        if (!numberOfReservations || !Number.isSafeInteger(numberOfReservations) || numberOfReservations < 1) {
            throw slugError('Invalid reservation request -> invalid number of reservations', ['reservator', 'request', 'constructor', 'error', 'numberofreservations']);
        }
        this.action = ReservationRequestAction.Reserve;
        this.type = type;
        this.guid = guid;
        this.numberOfReservations = numberOfReservations;
        this.childType = undefined;
        this.childGuid = undefined;
        /**
         *  The executor creates the bridge between the promise internals and
         *  the resolve and reject methods exposed on the reservation request instance.
         *
         *  @internal
         */
        this._promise = new Promise((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
    }
    /**
     *  For child changes a reservation request to request a child reservation for the already present reservation
     *  This method can only be called once and can not be called if the reservation request is a release request
     */
    forChild(type, guid) {
        if (!(this instanceof ReservationRequest)) {
            throw slugError('Invalid reservation request -> for child called on invalid reservation request', ['reservator', 'request', 'forChild', 'error', 'reservationrequest']);
        }
        if (this.childType || this.childGuid) {
            throw slugError('Invalid reservation request -> can not combine child reservation and release', ['reservator', 'request', 'forChild', 'error', 'exclusive']);
        }
        if (!type || !enumIncludes(ReservationType, type)) {
            throw slugError('Invalid reservation request -> invalid child type', ['reservator', 'request', 'forChild', 'error', 'type']);
        }
        // noinspection SuspiciousTypeOfGuard
        if (!guid || typeof guid !== 'string' || !guid.length) {
            throw slugError('Invalid reservation request -> invalid child guid', ['reservator', 'request', 'forChild', 'error', 'guid']);
        }
        this.childType = type;
        this.childGuid = guid;
        return this;
    }
    /**
     *  Release changes a reservation request to release the already present reservation
     *  This method can only be called once and can not be called if the reservation request is a child reservation request
     */
    release(reservation) {
        if (!(this instanceof ReservationRequest)) {
            throw slugError('Invalid reservation request -> release called on invalid reservation request', ['reservator', 'request', 'release', 'error', 'reservationrequest']);
        }
        if (this.childType || this.childGuid) {
            throw slugError('Invalid reservation request -> can not combine release and child reservation', ['reservator', 'request', 'release', 'error', 'exclusive']);
        }
        // noinspection SuspiciousTypeOfGuard
        if (!reservation || typeof reservation !== 'string' || !reservation.length) {
            throw slugError('Invalid reservation request -> invalid reservation key', ['reservator', 'request', 'release', 'error', 'reservation']);
        }
        this.action = ReservationRequestAction.Release;
        this.childType = undefined;
        this.childGuid = reservation;
        return this;
    }
    /**
     *  Handler to resolve the underlying promise
     */
    resolve(value) {
        this._resolve(value);
    }
    /**
     *  Handler to reject the underlying promise
     */
    reject(reason) {
        this._reject(reason);
    }
}
//# sourceMappingURL=reservationRequest.js.map